import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardList, ShieldCheck, Upload } from 'lucide-react';
import { getDocumentTypes } from '../../api/documentTypes';
import { uploadDocumentFile, uploadDriverDocument } from '../../api/drivers';
import { useToast } from '../../context/ToastContext';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { getErrorMessage, getStatusColor } from '../../lib/utils';

// Same constraints the backend enforces on /upload/document (and the driver app).
const ACCEPT = '.jpg,.jpeg,.png,.pdf';
const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'pdf'];
const MAX_SIZE = 5 * 1024 * 1024;

function DocumentCard({ type, current, uploading, disabled, onPick }) {
  const hasDoc = !!current;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{type.title}</span>
          {type.is_mandatory && <Badge variant="warning">Required</Badge>}
          {hasDoc && <Badge variant={getStatusColor(current.status)}>{current.status}</Badge>}
        </div>
        {type.description && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{type.description}</p>
        )}
        {hasDoc && current.status === 'rejected' && current.rejection_reason && (
          <p className="mt-0.5 text-xs text-red-500">Rejected: {current.rejection_reason}</p>
        )}
      </div>
      <Button
        size="sm"
        variant={hasDoc ? 'secondary' : 'primary'}
        icon={Upload}
        loading={uploading}
        disabled={disabled}
        onClick={onPick}
      >
        {hasDoc ? 'Replace' : 'Upload'}
      </Button>
    </div>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <div className="space-y-3">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <Icon size={14} /> {label}
      </h3>
      {children}
    </div>
  );
}

export default function UploadDocumentsModal({ isOpen, onClose, driverId, docs }) {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const fileInputRef = useRef(null);
  const pickedTypeRef = useRef(null);
  const [uploadingTypeId, setUploadingTypeId] = useState(null);

  // Shares its cache entry with the document list behind this modal.
  const { data: typesData, isLoading } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => getDocumentTypes({ include_inactive: true }).then((r) => r.data),
    enabled: isOpen,
  });
  const types = (Array.isArray(typesData) ? typesData : (typesData?.items ?? [])).filter((t) => t.is_active);
  const mandatory = types.filter((t) => t.is_mandatory);
  const optional = types.filter((t) => !t.is_mandatory);

  // A type can have several uploads over time; the newest one is what counts.
  const latestByType = {};
  [...docs].sort((a, b) => a.id - b.id).forEach((d) => { latestByType[d.doc_type_id] = d; });
  const submittedRequired = mandatory.filter((t) => latestByType[t.id] && latestByType[t.id].status !== 'rejected').length;

  const upload = useMutation({
    mutationFn: async ({ typeId, file }) => {
      const { data } = await uploadDocumentFile(file);
      return uploadDriverDocument(driverId, { doc_type_id: typeId, file_url: data.file_url });
    },
    onSuccess: (_, { typeTitle }) => {
      qc.invalidateQueries({ queryKey: ['driver-documents', driverId] });
      // Uploading a new mandatory document resets approval, so refresh the drivers list too.
      qc.invalidateQueries({ queryKey: ['drivers'] });
      addToast(`${typeTitle} uploaded — pending review`, 'success');
    },
    onError: (err) => addToast(getErrorMessage(err, 'Upload failed'), 'error'),
    onSettled: () => setUploadingTypeId(null),
  });

  function pick(type) {
    pickedTypeRef.current = type;
    fileInputRef.current?.click();
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    const type = pickedTypeRef.current;
    if (!file || !type) return;

    const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
    if (!ALLOWED_EXT.includes(ext)) {
      addToast('Only JPG, PNG or PDF files are allowed', 'error');
      return;
    }
    if (file.size > MAX_SIZE) {
      addToast('File size exceeds the 5MB limit', 'error');
      return;
    }
    setUploadingTypeId(type.id);
    upload.mutate({ typeId: type.id, typeTitle: type.title, file });
  }

  const busy = uploadingTypeId !== null;
  const allRequiredDone = mandatory.length > 0 && submittedRequired === mandatory.length;

  const renderCards = (list) =>
    list.map((t) => (
      <DocumentCard
        key={t.id}
        type={t}
        current={latestByType[t.id]}
        uploading={uploadingTypeId === t.id}
        disabled={busy && uploadingTypeId !== t.id}
        onPick={() => pick(t)}
      />
    ));

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title="Upload Documents" size="md">
      <input ref={fileInputRef} type="file" accept={ACCEPT} className="hidden" onChange={handleFile} />

      {isLoading ? (
        <p className="py-8 text-center text-sm text-gray-400">Loading document types…</p>
      ) : types.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">No active document types. Add one on the Document Types page first.</p>
      ) : (
        <div className="space-y-6">
          <div
            className={
              'flex items-center gap-3 rounded-xl border p-3 text-sm ' +
              (allRequiredDone
                ? 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
                : 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400')
            }
          >
            {allRequiredDone ? <CheckCircle2 size={18} /> : <ClipboardList size={18} />}
            <span>
              {mandatory.length === 0
                ? 'No required documents configured'
                : allRequiredDone
                  ? 'All required documents submitted'
                  : `${submittedRequired} of ${mandatory.length} required documents submitted`}
            </span>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            JPG, PNG or PDF, up to 5MB. Uploaded documents are marked pending until reviewed.
          </p>

          {mandatory.length > 0 && (
            <Section icon={ShieldCheck} label="Required documents">{renderCards(mandatory)}</Section>
          )}
          {optional.length > 0 && (
            <Section icon={ClipboardList} label="Optional documents">{renderCards(optional)}</Section>
          )}
        </div>
      )}
    </Modal>
  );
}
